/**
 * Promoção por quantidade (PRAT-184): "2º item com X%" / "3º item com X%".
 *
 * Espelho do algoritmo da API (`QuantityPromotionCalculator.php`). A API é a
 * fonte da verdade — o app só mostra a prévia; o valor nunca é enviado no
 * pedido do app.
 *
 * Regra:
 * 1. Sem regra, percentual <= 0 ou canal fora de `channels` → nada.
 * 2. Participa a linha com `item_id` fora de `excluded_item_ids`, `category_id`
 *    em `category_ids` (lista vazia = todas), `unit_price` > 0 e não pesável.
 * 3. Expande em unidades (quantidade 2 = 2 unidades), ordena por preço
 *    unitário DECRESCENTE (empate: ordem do carrinho) e forma grupos
 *    consecutivos de `group_size`.
 * 4. Em cada grupo COMPLETO, a ÚLTIMA unidade (a mais barata do grupo) ganha
 *    `round(unit_price * percentage / 100, 2)`. Soma por linha. As unidades
 *    que sobram são as mais baratas. Ex.: [10, 40, 20, 30] com 50% → grupos
 *    [40, 30] e [20, 10] → 15 (linha do 30) + 5 (linha do 10) = 20.
 * 5. `missing_for_next` = quantas unidades faltam para fechar o próximo grupo
 *    (null quando fechou exato ou quando não há unidade participante).
 */

export type QuantityPromotionChannel = 'app' | 'garcon' | 'nova_venda' | 'pdv' | 'terminal';

export type QuantityPromotionType = 'second_item' | 'third_item';

export type QuantityPromotionRule = {
  type: QuantityPromotionType;
  group_size: number; // 2 | 3
  percentage: number | string; // 0–100 (a API pode mandar decimal como string)
  name: string;
  channels: QuantityPromotionChannel[] | string[];
  category_ids: string[];
  excluded_item_ids: string[];
  is_active?: boolean;
};

export type QuantityPromotionLine = {
  key: string; // identificador da linha do carrinho (volta em `lines`)
  item_id: string;
  category_id?: string | null;
  quantity: number;
  unit_price: number; // preço unitário TOTAL da linha (De/Por + complementos + extras)
  weighted?: boolean; // pesável não participa
};

export type QuantityPromotionResult = {
  applies: boolean;
  total: number;
  lines: Record<string, number>; // desconto por linha (key → valor)
  eligible_keys: string[]; // linhas que participam da promoção (key)
  eligible_units: number;
  discounted_units: number;
  group_size: number;
  missing_for_next: number | null;
  name: string;
};

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const toNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

function resolveGroupSize(rule: QuantityPromotionRule): number {
  const size = Math.trunc(toNumber(rule.group_size));
  if (size >= 2) return size;
  return rule.type === 'third_item' ? 3 : 2;
}

function emptyResult(rule: QuantityPromotionRule | null | undefined): QuantityPromotionResult {
  return {
    applies: false,
    total: 0,
    lines: {},
    eligible_keys: [],
    eligible_units: 0,
    discounted_units: 0,
    group_size: rule ? resolveGroupSize(rule) : 2,
    missing_for_next: null,
    name: rule?.name ?? '',
  };
}

/**
 * A regra vale neste canal? (ativa, percentual > 0 e canal em `channels`).
 * É a mesma triagem do início do `calculateQuantityPromotion`.
 */
export function isQuantityPromotionActive(
  rule: QuantityPromotionRule | null | undefined,
  channel: QuantityPromotionChannel
): rule is QuantityPromotionRule {
  if (!rule || rule.is_active === false) return false;
  if (toNumber(rule.percentage) <= 0) return false;
  const channels = Array.isArray(rule.channels) ? rule.channels : [];
  return channels.includes(channel);
}

/**
 * O produto participa da promoção? Categoria em `category_ids` (lista vazia =
 * todas) e id fora de `excluded_item_ids`. Não olha preço nem quantidade —
 * serve para o selo do catálogo e a linha do carrinho.
 */
export function isItemInQuantityPromotion(
  rule: QuantityPromotionRule | null | undefined,
  item: { id: string; category_id?: string | null },
  channel: QuantityPromotionChannel = 'app'
): boolean {
  if (!isQuantityPromotionActive(rule, channel)) return false;
  const excluded = Array.isArray(rule.excluded_item_ids) ? rule.excluded_item_ids : [];
  if (excluded.includes(item.id)) return false;
  const categories = Array.isArray(rule.category_ids) ? rule.category_ids : [];
  if (categories.length === 0) return true;
  return categories.includes(item.category_id ?? '');
}

export function calculateQuantityPromotion(
  rule: QuantityPromotionRule | null | undefined,
  lines: QuantityPromotionLine[],
  channel: QuantityPromotionChannel
): QuantityPromotionResult {
  if (!rule || rule.is_active === false) return emptyResult(rule);

  const percentage = toNumber(rule.percentage);
  if (percentage <= 0) return emptyResult(rule);

  const channels = Array.isArray(rule.channels) ? rule.channels : [];
  if (!channels.includes(channel)) return emptyResult(rule);

  const groupSize = resolveGroupSize(rule);
  const excluded = new Set(Array.isArray(rule.excluded_item_ids) ? rule.excluded_item_ids : []);
  const categories = Array.isArray(rule.category_ids) ? rule.category_ids : [];

  // Expande em unidades, na ordem do carrinho (desempate estável no sort).
  const units: { key: string; unit_price: number }[] = [];
  const eligibleKeys: string[] = [];
  for (const line of lines) {
    if (line.weighted) continue;
    if (excluded.has(line.item_id)) continue;
    if (categories.length > 0 && !categories.includes(line.category_id ?? '')) continue;
    const unitPrice = toNumber(line.unit_price);
    if (unitPrice <= 0) continue;
    const quantity = Math.trunc(toNumber(line.quantity));
    if (quantity <= 0) continue;
    if (!eligibleKeys.includes(line.key)) eligibleKeys.push(line.key);
    for (let i = 0; i < quantity; i++) units.push({ key: line.key, unit_price: unitPrice });
  }

  const eligibleUnits = units.length;
  const result = emptyResult(rule);
  result.eligible_keys = eligibleKeys;
  result.eligible_units = eligibleUnits;
  if (eligibleUnits === 0) return result;

  const remainder = eligibleUnits % groupSize;
  result.missing_for_next = remainder === 0 ? null : groupSize - remainder;

  const discountedUnits = Math.floor(eligibleUnits / groupSize);
  result.discounted_units = discountedUnits;
  if (discountedUnits === 0) return result;

  // Ordena por preço decrescente (`sort` é estável: o empate mantém a ordem
  // do carrinho) e desconta a última unidade de cada grupo completo — a mais
  // barata do grupo. Quem sobra são as unidades mais baratas.
  const sorted = [...units].sort((a, b) => b.unit_price - a.unit_price);

  let total = 0;
  for (let g = 0; g < discountedUnits; g++) {
    const unit = sorted[g * groupSize + groupSize - 1];
    const discount = round2((unit.unit_price * percentage) / 100);
    result.lines[unit.key] = round2((result.lines[unit.key] ?? 0) + discount);
    total += discount;
  }

  result.total = round2(total);
  result.applies = result.total > 0;
  return result;
}

/** "50" → "50%"; "33.5" → "33,5%". */
export function formatQuantityPromotionPercentage(percentage: number | string): string {
  const value = toNumber(percentage);
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

/** "2º" / "3º" conforme o tamanho do grupo. */
export function quantityPromotionOrdinal(groupSize: number): string {
  return `${Math.max(2, Math.trunc(toNumber(groupSize)) || 2)}º`;
}

/** Rótulo da linha com desconto: "2º item com 50%". */
export function quantityPromotionLabel(rule: QuantityPromotionRule): string {
  return `${quantityPromotionOrdinal(resolveGroupSize(rule))} item com ${formatQuantityPromotionPercentage(rule.percentage)}`;
}

/** Selo curto do catálogo: "2º com 50%" / "3º com 30%". */
export function quantityPromotionChipLabel(rule: QuantityPromotionRule): string {
  return `${quantityPromotionOrdinal(resolveGroupSize(rule))} com ${formatQuantityPromotionPercentage(rule.percentage)}`;
}

/**
 * Lista de categorias em texto: "Hambúrguers", "Hambúrguers e Pizzas",
 * "Hambúrguers, Pizzas e Porções"; acima de 3, "Hambúrguers, Pizzas e mais 2".
 */
export function formatCategoryNames(names: string[]): string {
  const clean = names.map(name => String(name ?? '').trim()).filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  if (clean.length <= 3) return `${clean.slice(0, -1).join(', ')} e ${clean[clean.length - 1]}`;
  return `${clean[0]}, ${clean[1]} e mais ${clean.length - 2}`;
}

/**
 * Onde a promoção vale: "Vale para: Hambúrguers". Sem categoria escolhida
 * (`category_ids` vazio) vale para o cardápio todo; com categorias mas sem
 * nome resolvido (cardápio ainda não carregado) devolve null.
 */
export function quantityPromotionScopeLabel(
  rule: QuantityPromotionRule | null | undefined,
  categoryNames: string[]
): string | null {
  if (!rule) return null;
  const categories = Array.isArray(rule.category_ids) ? rule.category_ids : [];
  if (categories.length === 0) return 'Vale para todo o cardápio';
  const names = formatCategoryNames(categoryNames);
  return names ? `Vale para: ${names}` : null;
}

/**
 * Aviso junto da linha participante: "Adicione mais 1 e ganhe 50% no 2º" /
 * "Adicione mais 2 e ganhe 30% no 3º". Mesma condição do `quantityPromotionHint`.
 */
export function quantityPromotionLineHint(
  result: QuantityPromotionResult | null | undefined,
  rule: QuantityPromotionRule | null | undefined
): string | null {
  if (!result || !rule) return null;
  const missing = result.missing_for_next;
  if (missing !== 1 && missing !== 2) return null;
  if (result.eligible_units < 1) return null;

  const percentage = formatQuantityPromotionPercentage(rule.percentage);
  const ordinal = quantityPromotionOrdinal(result.group_size);
  return `Adicione mais ${missing} e ganhe ${percentage} no ${ordinal}`;
}

/**
 * Aviso de "quase lá": "Falta 1 item para ganhar 50% no 2º" / "Faltam 2 itens
 * para ganhar 30% no 3º". Só quando faltam 1 ou 2 unidades e já há ao menos
 * uma unidade participante; senão null.
 */
export function quantityPromotionHint(
  result: QuantityPromotionResult | null | undefined,
  rule: QuantityPromotionRule | null | undefined
): string | null {
  if (!result || !rule) return null;
  const missing = result.missing_for_next;
  if (missing !== 1 && missing !== 2) return null;
  if (result.eligible_units < 1) return null;

  const percentage = formatQuantityPromotionPercentage(rule.percentage);
  const ordinal = quantityPromotionOrdinal(result.group_size);
  return missing === 1
    ? `Falta 1 item para ganhar ${percentage} no ${ordinal}`
    : `Faltam ${missing} itens para ganhar ${percentage} no ${ordinal}`;
}

export type QuantityPromotionCta = {
  /** "Falta 1 item para ganhar 50% no 2º!" */
  title: string;
  /** "Adicione mais um e o mais barato sai pela metade" */
  subtitle: string;
  missing: 1 | 2;
  /** Tamanho do grupo (2 = par, 3 = trio). */
  group_size: number;
};

/**
 * Chamada forte do carrinho (banner na cor da loja): título e subtexto.
 * Mesma condição do `quantityPromotionHint` — só quando faltam 1 ou 2 unidades.
 */
export function quantityPromotionCta(
  result: QuantityPromotionResult | null | undefined,
  rule: QuantityPromotionRule | null | undefined
): QuantityPromotionCta | null {
  if (!result || !rule) return null;
  const missing = result.missing_for_next;
  if (missing !== 1 && missing !== 2) return null;
  if (result.eligible_units < 1) return null;

  const percentage = formatQuantityPromotionPercentage(rule.percentage);
  const ordinal = quantityPromotionOrdinal(result.group_size);
  const gain =
    toNumber(rule.percentage) === 50 ? 'sai pela metade' : `sai com ${percentage} de desconto`;
  return missing === 1
    ? {
        title: `Falta 1 item para ganhar ${percentage} no ${ordinal}!`,
        subtitle: `Adicione mais um e o mais barato ${gain}`,
        missing: 1,
        group_size: result.group_size,
      }
    : {
        title: `Faltam 2 itens para ganhar ${percentage} no ${ordinal}!`,
        subtitle: `Adicione mais dois e o mais barato ${gain}`,
        missing: 2,
        group_size: result.group_size,
      };
}

/**
 * Texto do botão do banner: "Adicionar mais 1 de X-Tudo (fecha o par)" quando
 * falta 1; "Adicionar mais 1 de X-Tudo (faltam 2)" quando faltam 2.
 */
export function quantityPromotionAddLabel(
  cta: QuantityPromotionCta,
  itemName?: string | null
): string {
  const base = itemName ? `Adicionar mais 1 de ${itemName}` : 'Adicionar mais 1';
  if (cta.missing !== 1) return `${base} (faltam ${cta.missing})`;
  const group = cta.group_size === 2 ? 'o par' : cta.group_size === 3 ? 'o trio' : 'o grupo';
  return `${base} (fecha ${group})`;
}

/**
 * Linha participante MAIS BARATA (preço unitário; empate: ordem do carrinho).
 * É a que sobra fora dos grupos, e a que o banner oferece repetir —
 * "Adicionar mais 1 de {nome}".
 */
export function cheapestEligibleLineKey(
  result: QuantityPromotionResult | null | undefined,
  lines: QuantityPromotionLine[]
): string | null {
  if (!result || result.eligible_keys.length === 0) return null;
  const eligible = new Set(result.eligible_keys);
  let best: QuantityPromotionLine | null = null;
  for (const line of lines) {
    if (!eligible.has(line.key)) continue;
    if (!best || toNumber(line.unit_price) < toNumber(best.unit_price)) best = line;
  }
  return best?.key ?? null;
}
